import { useState, useEffect } from 'react';
import { User, Key, Save, Shield } from 'lucide-react';
import { profile as profileApi } from '../lib/api';
import { useAuth } from '../lib/auth';

const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Platform Administrator',
  ceo: 'CEO',
  director: 'Director',
  head_of_dept: 'Head of Department',
  finance: 'Finance',
  employee: 'Employee',
};

export default function Profile() {
  const { user, refresh } = useAuth();
  const [fullName, setFullName] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFullName(user.full_name || '');
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMsg(''); setSaveErr('');
    setSaveLoading(true);
    try {
      await profileApi.update(fullName);
      await refresh();
      setSaveMsg('Profile updated successfully!');
    } catch (err: any) {
      setSaveErr(err.message || 'Failed to update profile');
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(''); setPwErr('');
    if (newPassword.length < 8) { setPwErr('New password must be at least 8 characters'); return; }
    setPwLoading(true);
    try {
      await profileApi.changePassword(currentPassword, newPassword);
      setPwMsg('Password changed successfully!');
      setCurrentPassword(''); setNewPassword('');
    } catch (err: any) {
      setPwErr(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
        <User className="w-6 h-6 text-green-600" /> Profile & Settings
      </h1>

      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900">Account Details</h2>
        {saveMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{saveMsg}</div>}
        {saveErr && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{saveErr}</div>}
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="label">Full name</label>
            <input type="text" className="input-field" value={fullName} onChange={e => setFullName(e.target.value)} />
          </div>
          <div>
            <label className="label">Email</label>
            <input type="email" className="input-field bg-gray-50" value={user.email} disabled />
          </div>
          <div className="flex items-center gap-2 bg-green-50 text-green-700 rounded-lg px-3 py-2 text-sm w-fit">
            <Shield className="w-4 h-4" /> {ROLE_LABELS[user.role] || user.role}
          </div>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={saveLoading}>
            <Save className="w-4 h-4" /> {saveLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      <div className="card space-y-4">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Key className="w-4 h-4 text-green-600" /> Change Password</h2>
        {pwMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">{pwMsg}</div>}
        {pwErr && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{pwErr}</div>}
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="label">Current password</label>
            <input type="password" className="input-field" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
          </div>
          <div>
            <label className="label">New password</label>
            <input type="password" className="input-field" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8} />
          </div>
          <button type="submit" className="btn-secondary" disabled={pwLoading}>
            {pwLoading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
